import {
  LogoHauteGaspesie,
  LogoMutuoHorizontal,
  LogoRefrain,
  LogoSerresTessierHorizontal,
} from 'app/design/branding/logo';
import { LogoOneLine } from 'app/design/icons/logo-one-line';
import { LogoOneLinePartake } from 'app/design/icons/logo-one-line-partake';
import { Button } from 'app/design/primitives/button';
import { Button as DesignSystemButton } from 'app/shopify/components/actions/Button/Button';
import { Box, Row, Text, useTheme } from 'app/shopify';
import { useNavigation } from 'app/hooks/navigation';
import { authenticationGateSourceAtom, presentGuestSignupPromptAtom, useResetSearchAtoms } from 'app/atoms';
import { isWhiteLabel } from 'app/utils/helpers';
import { WhiteLabel } from 'app/types/white-labels';
import { env } from '../../env';
import { WHITE_LABEL } from '../../../../app.config.json';
import { HeaderSearchBar } from './header-search-bar';
import { HeaderRightDesktop } from './header-right';
import { PublishButton } from './publish-button';
import { IconHelpCircle } from '@tabler/icons-react-native';
import { getI18nLanguage, supportedLanguages, type Language } from 'app/lib/language';
import { i18n } from 'app/localization/i18n';
import { storage, StorageKey } from 'app/lib/storage';
import { useIsGuestWeb } from 'app/utils/web-helpers';
import { useTranslation } from 'react-i18next';
import type { ViewStyle } from 'react-native';
import { useWindowDimensions, View } from 'react-native';
import { useState } from 'react';
import { useSetAtom } from 'jotai';
const onboardingRoutes = [
  'contact-information',
  'credentials',
  'personal-information',
  'choose-sharer-type',
  'guidelines',
  'choose',
  'confirm',
  'thanks',
];

function GuestHeaderRight({ compact = false }: { compact?: boolean }) {
  const theme = useTheme();
  const navigation = useNavigation();
  const { t: tLogin } = useTranslation('login');
  const { t: tHeader } = useTranslation('header');
  const { t: tLanguage } = useTranslation('language');
  const setGuestSignupVisible = useSetAtom(presentGuestSignupPromptAtom);
  const setAuthenticationGateSource = useSetAtom(authenticationGateSourceAtom);
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const currentLanguage = getI18nLanguage();

  const showSignUp = () => {
    setAuthenticationGateSource('header');
    setGuestSignupVisible(true);
  };

  const selectLanguage = async (language: Language) => {
    setLanguageMenuOpen(false);
    await storage.saveString(StorageKey.PreferredLang, language);
    await i18n.changeLanguage(language);
  };

  return (
    <Row alignItems="center" flexShrink={0} gap="spacing.general.x4">
      <Row alignItems="center" gap="spacing.general.x1">
        <DesignSystemButton
          label={tLogin('signUpOrLogin')}
          onPress={showSignUp}
          size={compact ? 'small' : 'medium'}
          testID="guest-header-signup-button"
          variant="tertiary"
          inverted={theme.colors['white-label.header.on-header'] === theme.colors['text.inverse.primary']}
        />
      </Row>
      <Button
        accessibilityLabel={tHeader('helpButtonAccessibilityLabel')}
        onPress={() => navigation.navigate('help')}
        padding="spacing.general.x2"
        testID="guest-header-help-button"
        variant="plain"
      >
        <IconHelpCircle color={theme.colors['white-label.header.on-header']} size={24} />
      </Button>
      <Box position="relative" zIndex={40}>
        <Button
          accessibilityLabel={tLanguage('title')}
          onPress={() => setLanguageMenuOpen((open) => !open)}
          padding="spacing.general.x2"
          variant="plain"
        >
          <Row alignItems="center" gap="spacing.general.x1">
            {compact ? null : (
              <Text color="white-label.header.on-header" fontWeight="700">
                {currentLanguage.toUpperCase()}
              </Text>
            )}
          </Row>
        </Button>
        {languageMenuOpen ? (
          <Box
            backgroundColor="surface.default"
            borderRadius="radius.md"
            padding="spacing.general.x2"
            position="absolute"
            right={0}
            style={{ boxShadow: '0 8px 24px rgba(0, 0, 0, 0.16)', minWidth: 150 }}
            top="100%"
          >
            {supportedLanguages.map((language) => (
              <Button
                key={language}
                onPress={() => void selectLanguage(language)}
                padding="spacing.general.x2"
                variant="plain"
              >
                <Text color="text.primary" fontWeight={language === currentLanguage ? '700' : '400'}>
                  {tLanguage(`options.${language}`)}
                </Text>
              </Button>
            ))}
          </Box>
        ) : null}
      </Box>
    </Row>
  );
}

function DesktopHeaderLogo() {
  const theme = useTheme();
  const navigation = useNavigation();
  const resetSearchAtoms = useResetSearchAtoms();
  switch (WHITE_LABEL) {
    case WhiteLabel.Refrain:
      return (
        <Button
          onPress={() => {
            resetSearchAtoms();
            navigation.reset({ routes: [{ name: 'main' }] });
          }}
        >
          <LogoRefrain height={75} width={187} />
        </Button>
      );

    case WhiteLabel.SerresTessier:
      return <LogoSerresTessierHorizontal height={22} width={139} />;
    case WhiteLabel.HauteGaspesie:
      return (
        <Button
          onPress={() => {
            resetSearchAtoms();
            navigation.reset({ routes: [{ name: 'main' }] });
          }}
        >
          <LogoHauteGaspesie height={100} width={80} />
        </Button>
      );
    case WhiteLabel.Mutuo:
      return (
        <Button
          onPress={() => {
            resetSearchAtoms();
            navigation.reset({ routes: [{ name: 'main' }] });
          }}
        >
          <LogoMutuoHorizontal height={23} width={138} />
        </Button>
      );
  }

  return (
    <Button
      style={isWhiteLabel(WhiteLabel.SerresTessier) || isWhiteLabel(WhiteLabel.Mutuo) ? { marginTop: -5 } : {}}
      onPress={() => {
        resetSearchAtoms();
        navigation.reset({ routes: [{ name: 'main' }] });
      }}
    >
      {env.IS_PARTAKE ? <LogoOneLinePartake /> : <LogoOneLine color={theme.colors['white-label.header.on-header']} />}
    </Button>
  );
}

function getHeaderStyle(theme: ReturnType<typeof useTheme>, isGuestWeb: boolean, isNarrowGuestWeb: boolean): ViewStyle {
  return {
    flexDirection: 'row',
    alignItems: 'center',
    height: isNarrowGuestWeb ? 112 : isGuestWeb ? 72 : 56,
    paddingHorizontal: isNarrowGuestWeb ? theme.spacing['spacing.general.x2'] : theme.spacing['spacing.general.x5'],
    backgroundColor: theme.colors['white-label.header.bg'],
    boxShadow: '0 4px 4px 0 rgba(0, 0, 0, 0.08)',
    elevation: 3,
  };
}

function StandardHeaderContent({ isGuestWeb, isOnboardingRoute }: { isGuestWeb: boolean; isOnboardingRoute: boolean }) {
  return (
    <Row style={{ flexDirection: 'row', alignItems: 'center', flex: 1, maxWidth: 1920, marginHorizontal: 'auto' }}>
      <Box>
        <DesktopHeaderLogo />
      </Box>
      {isOnboardingRoute ? null : (
        <Row flex={1} justifyContent="flex-end" alignItems="center">
          <Row flex={1} marginRight={isGuestWeb ? 'spacing.general.x4' : 'spacing.general.x8'} alignItems="center">
            <HeaderSearchBar />
            <PublishButton />
          </Row>
          {isGuestWeb ? <GuestHeaderRight /> : <HeaderRightDesktop />}
        </Row>
      )}
    </Row>
  );
}

function DesktopHeader() {
  const theme = useTheme();
  const navigation = useNavigation();
  const currentRoute = navigation.getState().routes[navigation.getState().index];
  const isPasswordStack = navigation.getState().routes.some((route) => route.name === 'password-recovery');
  const isGuestWeb = useIsGuestWeb();
  const { width } = useWindowDimensions();
  const isNarrowGuestWeb = isGuestWeb && width < 768;

  const viewStyle = getHeaderStyle(theme, isGuestWeb, isNarrowGuestWeb);

  if (isPasswordStack) {
    return (
      <View style={viewStyle}>
        <View>
          <DesktopHeaderLogo />
        </View>
      </View>
    );
  }

  const isOnboardingRoute = onboardingRoutes.includes(currentRoute?.name ?? '');
  if (isNarrowGuestWeb && !isOnboardingRoute) {
    return (
      <Row style={viewStyle}>
        <Box flex={1} gap="spacing.general.x2" style={{ marginHorizontal: 'auto', maxWidth: 768 }}>
          <Row alignItems="center" justifyContent="space-between">
            <Box>
              <DesktopHeaderLogo />
            </Box>
            <GuestHeaderRight compact />
          </Row>
          <Row alignItems="center" flex={1}>
            <HeaderSearchBar />
            <PublishButton compact />
          </Row>
        </Box>
      </Row>
    );
  }

  if (width >= 768 && width < 1024 && !isOnboardingRoute) {
    return (
      <Box
        style={[
          viewStyle,
          { flexDirection: 'column', height: 128, paddingHorizontal: theme.spacing['spacing.general.x6'] },
        ]}
      >
        <Row width="100%" minHeight={72} alignItems="center" gap="spacing.general.x6">
          <Box>
            <DesktopHeaderLogo />
          </Box>
          <Row flex={1} alignItems="center" justifyContent="flex-end" gap="spacing.general.x4">
            <PublishButton compact />
            {isGuestWeb ? <GuestHeaderRight compact /> : <HeaderRightDesktop compact />}
          </Row>
        </Row>
        <HeaderSearchBar fullWidth />
        <Box height={theme.spacing['spacing.general.x2']} />
      </Box>
    );
  }

  return (
    <Row style={viewStyle}>
      <StandardHeaderContent isGuestWeb={isGuestWeb} isOnboardingRoute={isOnboardingRoute} />
    </Row>
  );
}

export function DesktopHeaderRenderer() {
  return <DesktopHeader />;
}
